// Shared helpers for the unlock, success, and manage pages.
// Each page bundles this module into its own webpack entry, so there is no
// cross-page runtime coupling — this only removes source duplication.

export function parseTestMode (value) {
  return value === true || value === 'true' || value === 'True'
}

export function sanitizeParam (value) {
  if (value == null || value === '') return undefined
  return String(value).trim().replace(/^['"]+|['"]+$/g, '')
}

export function hexToSoft (hex, opacity = 0.18) {
  const m = /^#([0-9a-f]{6})$/i.exec(hex || '')
  if (!m) return `rgba(110, 184, 235, ${opacity})`
  const r = parseInt(m[1].slice(0, 2), 16)
  const g = parseInt(m[1].slice(2, 4), 16)
  const b = parseInt(m[1].slice(4, 6), 16)
  return `rgba(${r}, ${g}, ${b}, ${opacity})`
}

export function escapeHtml (value) {
  return String(value).replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[c]))
}

// The unlock and manage pages share the `unlock__*` brand markup: an optional
// logo <img> inserted ahead of the brand name, with the name shown only when
// there is no logo. Both directions must be handled or a stale logo survives a
// branding change.
export function showBrandLogo (branding, { brandEl, brandNameEl, logoClass = 'unlock__brand-logo' }) {
  let img = brandEl.querySelector(`.${logoClass}`)
  if (!img) {
    img = document.createElement('img')
    img.className = logoClass
    brandEl.insertBefore(img, brandNameEl)
  }
  img.src = branding.logoUrl
  img.alt = branding.extensionName || 'Logo'
  brandNameEl.hidden = true
  brandNameEl.textContent = ''
}

export function hideBrandLogo (branding, { brandEl, brandNameEl, logoClass = 'unlock__brand-logo' }) {
  brandEl.querySelector(`.${logoClass}`)?.remove()

  const name = branding.extensionName || ''
  brandNameEl.hidden = !name
  brandNameEl.textContent = name
}

export function applyTestModeBadge (rootEl, testBadgeEl, testMode, testModeClass) {
  rootEl.classList.toggle(testModeClass, testMode)
  if (testBadgeEl) testBadgeEl.hidden = !testMode
}

// Generic branding application: sets the accent color CSS vars, toggles the
// has-logo class, then delegates the logo/name DOM work and any
// page-specific fields (tagline, footer note, success copy, etc.) to the
// caller so each page's exact original behavior is preserved.
export function applyBranding (branding, {
  rootEl,
  accentVarName,
  accentSoftVarName,
  hasLogoClass,
  softOpacity = 0.18,
  applyLogo,
  applyNoLogo,
  extra
}) {
  if (!branding) return

  if (branding.primaryColor) {
    rootEl.style.setProperty(accentVarName, branding.primaryColor)
    rootEl.style.setProperty(accentSoftVarName, hexToSoft(branding.primaryColor, softOpacity))
  }

  const hasLogo = Boolean(branding.logoUrl)
  rootEl.classList.toggle(hasLogoClass, hasLogo)

  if (hasLogo) {
    applyLogo(branding)
  } else if (applyNoLogo) {
    applyNoLogo(branding)
  }

  if (extra) extra(branding)
}
